// Time utility functions for consistent time display across the application

/**
 * Convert UTC ISO string to local time string
 * @param {string} isoString - ISO format time string (with or without Z)
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted local time string
 */
export const formatLocalTime = (isoString, options = {}) => {
  if (!isoString) return '-';
  
  try {
    // Ensure the string has Z (UTC) marker if it doesn't have timezone info
    let dateString = isoString;
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      // If no timezone info, assume it's UTC and add Z
      dateString = dateString + 'Z';
    }
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', isoString);
      return '-';
    }
    
    // Default options for Chinese locale
    const defaultOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      ...options
    };
    
    return date.toLocaleString('zh-CN', defaultOptions);
  } catch (e) {
    console.error('Error formatting time:', isoString, e);
    return '-';
  }
};

/**
 * Format time for display (date and time)
 * @param {string} isoString - ISO format time string
 * @returns {string} - Formatted time string
 */
export const formatDateTime = (isoString) => {
  return formatLocalTime(isoString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

/**
 * Format time for display (date only)
 * @param {string} isoString - ISO format time string
 * @returns {string} - Formatted date string
 */
export const formatDate = (isoString) => {
  return formatLocalTime(isoString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Format time for display (time only)
 * @param {string} isoString - ISO format time string
 * @returns {string} - Formatted time string
 */
export const formatTime = (isoString) => {
  return formatLocalTime(isoString, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

